import React from 'react';
import { BUSINESS, GRIEVANCE, fullAddress, mapEmbedUrl } from './businessInfo';

// Contact us. A gateway reviewer checks that a real, reachable address and phone
// number are shown — and that they are the same ones on the payment gateway
// application. Generic or unmonitored contacts get the page rejected.
//
// Every row here is conditional on the detail existing in businessInfo.js: while
// the restaurant has no monitored email address, published hours or named
// grievance officer, those rows and sections are simply not on the page.
//
// Sections become cards on a computer; the one holding the contact rows is marked
// --plain, because those rows are already cards of their own and spread into
// columns there.

const telHref = (number) => 'tel:' + String(number).replace(/[^\d+]/g, '');

const ContactUs = () => (
    <div className="so-infopage">
        <section className="so-infopage__section so-infopage__section--plain">
            <h3>Talk to us</h3>
            <p>
                For an order you have placed, a change to a special order, or anything
                that went wrong, call us on the number below — that is the fastest and
                surest way to reach the restaurant.
            </p>

            <div className="so-infopage__contact">
                {BUSINESS.phone && (
                    <a className="so-infopage__row" href={telHref(BUSINESS.phone)}>
                        <i className="pi pi-phone" />
                        <span><strong>{BUSINESS.phone}</strong><small>Call us</small></span>
                    </a>
                )}

                {BUSINESS.whatsapp && (
                    <a className="so-infopage__row" href={telHref(BUSINESS.whatsapp)}>
                        <i className="pi pi-comment" />
                        <span><strong>{BUSINESS.whatsapp}</strong><small>WhatsApp</small></span>
                    </a>
                )}

                {BUSINESS.email && (
                    <a className="so-infopage__row" href={'mailto:' + BUSINESS.email}>
                        <i className="pi pi-envelope" />
                        <span><strong>{BUSINESS.email}</strong><small>Email us</small></span>
                    </a>
                )}

                <div className="so-infopage__row">
                    <i className="pi pi-map-marker" />
                    <span>
                        <strong>{fullAddress()}</strong>
                        <small>Visit us</small>
                    </span>
                </div>

                {BUSINESS.hours && (
                    <div className="so-infopage__row">
                        <i className="pi pi-clock" />
                        <span><strong>{BUSINESS.hours}</strong><small>Opening hours</small></span>
                    </div>
                )}
            </div>
        </section>

        {/* The map pins the address above rather than coordinates of its own, so
            the two can never disagree. Lazy: a customer opening this page to find
            a phone number should not wait for map tiles. */}
        <section className="so-infopage__section so-infopage__section--plain">
            <h3>Find us</h3>
            <div className="so-infopage__map">
                <iframe
                    title={`Map of ${BUSINESS.fullName}`}
                    src={mapEmbedUrl()}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                />
            </div>
            {BUSINESS.mapLink && (
                <p className="so-infopage__maplink">
                    <a href={BUSINESS.mapLink} target="_blank" rel="noopener noreferrer">
                        <i className="pi pi-external-link" />Open in Google Maps
                    </a>
                </p>
            )}
        </section>

        {GRIEVANCE.name && (
            <section className="so-infopage__section">
                <h3>If something is not resolved</h3>
                <p>
                    If your question has not been answered to your satisfaction, escalate it
                    to our grievance officer. We aim to respond within{' '}
                    {GRIEVANCE.responseHours} hours of receiving an escalation.
                </p>
                <ul className="so-infopage__facts">
                    <li>
                        <span>Name</span>
                        <strong>{GRIEVANCE.name}</strong>
                    </li>
                    <li>
                        <span>Designation</span>
                        <strong>{GRIEVANCE.designation}</strong>
                    </li>
                    {GRIEVANCE.email && (
                        <li>
                            <span>Email</span>
                            <strong>{GRIEVANCE.email}</strong>
                        </li>
                    )}
                    {GRIEVANCE.phone && (
                        <li>
                            <span>Phone</span>
                            <strong>{GRIEVANCE.phone}</strong>
                        </li>
                    )}
                </ul>
            </section>
        )}

        <p className="so-infopage__note">
            Payments on this site are handled by our payment gateway. We never ask for
            your card number, CVV, PIN or any OTP over the phone, by email or on
            WhatsApp — if someone claiming to be from {BUSINESS.brandName} asks for them,
            it is not us.
        </p>
    </div>
);

export default ContactUs;
